import React, { Component } from 'react';
import ImageMap from '../../../Viewer/PracticeViewer/Map/ImageMap';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { practiceAreaStyles, selectedAreaStyles } from '../../../Viewer/PracticeViewer/Map/mapStyles/mapStyles';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { unByKey } from 'ol/Observable';
import { PracticeAreaMapProps } from '../types';
import { PracticeMapState, Field } from '../../../Viewer/PracticeViewer/types';
import { EventsKey } from 'ol/events';
import Geometry from 'ol/geom/Geometry';
import { Map } from 'ol';
import RenderFeature from 'ol/render/Feature';
import { Coordinate } from 'ol/coordinate';

const AREA_WIDTH = 550;
const AREA_HEIGHT = 240;

type PAFeature = Feature<Geometry> | RenderFeature | undefined;
type OLEvent = EventsKey | undefined;

const handleAreaResizing = (newCoord: Coordinate, index: number, coordinates: Array<Coordinate>, feature: PAFeature) => {
  const modifiedCoords = [...coordinates];
  const lowerIndex = index - 1;
  const upperIndex = index + 1;

  if (index === 0 || index === coordinates.length - 1) {
    modifiedCoords[0] = newCoord;
    modifiedCoords[coordinates.length - 1] = newCoord;
    modifiedCoords[1] = [modifiedCoords[1][0], newCoord[1]];
    modifiedCoords[coordinates.length - 2] = [newCoord[0], modifiedCoords[coordinates.length - 2][1]];
  } else if (upperIndex === coordinates.length - 1) {
    modifiedCoords[index] = newCoord;
    modifiedCoords[lowerIndex] = [modifiedCoords[lowerIndex][0], newCoord[1]];
    modifiedCoords[0] = [newCoord[0], modifiedCoords[upperIndex][1]];
    modifiedCoords[upperIndex] = [newCoord[0], modifiedCoords[upperIndex][1]];
  } else if (lowerIndex === 0) {
    modifiedCoords[index] = newCoord;
    modifiedCoords[lowerIndex] = [modifiedCoords[lowerIndex][0], newCoord[1]];
    modifiedCoords[coordinates.length - 1] = [modifiedCoords[coordinates.length - 1][0], newCoord[1]];
    modifiedCoords[upperIndex] = [newCoord[0], modifiedCoords[upperIndex][1]];
  } else {
    modifiedCoords[index] = newCoord;
    modifiedCoords[lowerIndex] = [newCoord[0], modifiedCoords[lowerIndex][1]];
    modifiedCoords[upperIndex] = [modifiedCoords[upperIndex][0], newCoord[1]];
  }

  (feature!.getGeometry() as Polygon).setCoordinates([modifiedCoords]);
};

const geometryCoordinatesToExtent = (coordinates: Array<Coordinate>) => {
  // eslint-disable-next-line no-unused-vars
  const [firstValue, ...modifiedCoords] = coordinates;
  let [x0, y0] = modifiedCoords[0];
  let [x1, y1] = modifiedCoords[0];

  modifiedCoords.forEach(coord => {
    if (coord[0] <= x0) x0 = coord[0];
    if (coord[1] <= y0) y0 = coord[1];
    if (coord[0] >= x1) x1 = coord[0];
    if (coord[1] >= y1) y1 = coord[1];
  });
  return [x0, y0, x1, y1];
};

const sortExtent = (extent: number[]) => {
  const modifiedExtent = [...extent];
  const [x0, y0, x1, y1] = extent;

  if (x0 > x1) [modifiedExtent[0], modifiedExtent[2]] = [modifiedExtent[2], modifiedExtent[0]];
  if (y0 > y1) [modifiedExtent[1], modifiedExtent[3]] = [modifiedExtent[3], modifiedExtent[1]];
  return modifiedExtent;
};

export default class PracticeAreaMap extends Component<PracticeAreaMapProps, PracticeMapState> {
  constructor (props: PracticeAreaMapProps) {
    super(props);

    this.mapRef = React.createRef();

    this.fieldSource = undefined;
    this.fieldLayer = undefined;

    this.modifyArea = undefined;
    this.shouldDraw = undefined;
  }

  /* Global Classes Types */
  mapRef: React.Ref<HTMLDivElement>;
  fieldSource: VectorSource | undefined;
  fieldLayer: VectorLayer<VectorSource<Geometry>> | undefined;
  modifyArea: OLEvent; // search later on
  shouldDraw: OLEvent; // search later on

  state: PracticeMapState = {
    practiceMap: undefined,
  };

  componentDidUpdate (prevProps: PracticeAreaMapProps, prevState: PracticeMapState) {
    if (
      prevProps.areaCoords !== this.props.areaCoords ||
      prevProps.selectedField !== this.props.selectedField ||
      prevProps.imageDimensions !== this.props.imageDimensions
    ) {
      this.removeDuplicateAreas();

      if (
        (this.props.selectedField &&
        this.props.selectedField._id === this.props.field._id) ||
        prevProps.areaCoords !== this.props.areaCoords
      ) {
        const [x0, y0, x1, y1] = this.props.areaCoords;

        if (x0 > x1 || y0 > y1) {
          const sortedExtent = sortExtent(this.props.areaCoords);
          this.props.setAreaCoords(sortedExtent);
        } else {
          const { imageDimensions } = this.props;
          const [, imgHeight] = imageDimensions;
          const imgVar = imgHeight / AREA_HEIGHT;

          const extent = [x0, y0, x1, y1];
          const formatedExtent = extent.map(x => Math.round(x / imgVar));

          this.drawSelectedFieldExtent(this.extentToCoordinates(formatedExtent));
        }
      }
    }
  }

  shouldDrawInArea = () => {
    const { practiceMap } = this.state;

    return practiceMap!.on('pointermove', (e) => {
      const feature = practiceMap!.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
        return feature;
      });

      if (feature) {
        const coords = (feature.getGeometry() as Polygon).getCoordinates();

        if (Array.isArray(coords[0])) {
          const [xClick, yClick] = e.coordinate;
          let hit = false;
          coords[0].forEach((coord, index) => {
            const [xCoord, yCoord] = coord;
            if (
              xClick + 5 >= xCoord && xClick - 5 <= xCoord &&
              yClick + 5 >= yCoord && yClick - 5 <= yCoord
            ) {
              hit = true;
            }
          });
          if (hit) {
            practiceMap!.getViewport().style.cursor = 'pointer';
          }
          else {
            practiceMap!.getViewport().style.cursor = '';
          }
        }
      } else {
        practiceMap!.getViewport().style.cursor = '';
      }
    });
  };

  modifyAreaCorners = (vectorSource: VectorSource) => {
    const { practiceMap } = this.state;
    let pointerMoveEvent: OLEvent;

    return practiceMap!.on('click', (e) => {
      if (!pointerMoveEvent) {
        const feature: PAFeature = practiceMap!.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
          return feature;
        });

        if (feature) {
          const coords = (feature.getGeometry() as Polygon).getCoordinates();

          if (Array.isArray(coords[0])) {
            const [xClick, yClick] = e.coordinate;
            coords[0].forEach((coord, index) => {
              const [xCoord, yCoord] = coord;
              if (
                !pointerMoveEvent &&
                xClick + 5 >= xCoord && xClick - 5 <= xCoord &&
                yClick + 5 >= yCoord && yClick - 5 <= yCoord
                ) {
                  this.fieldLayer!.setStyle(selectedAreaStyles);
                  pointerMoveEvent = practiceMap!.on('pointermove', e => {
                    handleAreaResizing(e.coordinate, index, coords[0], feature);
                  });
              }
            });
          }
        }
      } else {
        unByKey(pointerMoveEvent);
        pointerMoveEvent = undefined;

        if (vectorSource.getFeatures().length) {
          const vectorGeometry = vectorSource.getFeatures()[0].getGeometry() as Polygon;
          const coords = vectorGeometry.getCoordinates();
          const extentedCoords = geometryCoordinatesToExtent(coords[0]);
          const sortedCoords = this.extentToCoordinates(extentedCoords);
          vectorGeometry.setCoordinates([sortedCoords]);
          this.getRectangleCoordinates(vectorSource.getFeatures()[0]);
        }
      }
    });
  };

  getRectangleCoordinates = (feature: Feature<Geometry>) => {
    if (feature) {
      const { imageDimensions, setAreaCoords } = this.props;
      const [, imgHeight] = imageDimensions;
      const imgVar = imgHeight / AREA_HEIGHT;

      const coordinates = (feature.getGeometry() as Polygon).getExtent();
      const imageCoordinates = coordinates.map(x => Math.round(x * imgVar));
      const formatedImgCoords = this.setImgCoordinatesFormat(imageCoordinates, imageDimensions);

      setAreaCoords(formatedImgCoords);
    }
  };

  removeDuplicateAreas = () => {
    if (this.modifyArea) unByKey(this.modifyArea);
    if (this.shouldDraw) unByKey(this.shouldDraw);
    this.modifyArea = undefined;
    this.shouldDraw = undefined;

    if (this.fieldLayer) {
      this.fieldSource!.clear();
      this.state.practiceMap!.removeLayer(this.fieldLayer);

      this.fieldLayer = undefined;
      this.fieldSource = undefined;
    }
  };

  drawSelectedFieldExtent = (coordinates: Array<Coordinate>) => {
    const square = new Polygon([coordinates]);
    const squareFeature = new Feature(square);

    this.fieldSource = new VectorSource({ features: [squareFeature] });
    this.fieldLayer = new VectorLayer({ source: this.fieldSource, style: practiceAreaStyles });

    this.modifyArea = this.modifyAreaCorners(this.fieldSource);
    this.shouldDraw = this.shouldDrawInArea();
    this.state.practiceMap!.addLayer(this.fieldLayer);
  };

  setImgCoordinatesFormat = (imgCoords: number[], imageDimensions: number[]) => {
    let [x0, y0, x1, y1] = imgCoords;
    const [imgWidth, imgHeight] = imageDimensions;

    if (x0 < 0) x0 = 0;
    if (y0 < 0) y0 = 0;
    if (x1 > imgWidth) x1 = imgWidth;
    if (y1 > imgHeight) y1 = imgHeight;

    return [x0, y0, x1, y1];
  };

  extentToCoordinates = (extent: number[]) => {
    const minPoint = [extent[0], extent[1]];
    const maxPoint = [extent[2], extent[3]];
    const bottomRightPoint = [maxPoint[0], minPoint[1]];
    const topLeftPoint = [minPoint[0], maxPoint[1]];

    return [minPoint, bottomRightPoint, maxPoint, topLeftPoint, minPoint];
  };

  render () {
    const { practiceMap } = this.state;
    const { field, setField } = this.props;

    return (
      <div className="practice-area-map" ref={this.mapRef}>
        <ImageMap
          field={field as Field}
          setField={setField}
          mapImage={field.image}
          dimensions={[AREA_WIDTH, AREA_HEIGHT]}
          practiceMap={practiceMap}
          setPracticeMap={(map: Map) => this.setState({ ...this.state, practiceMap: map })}
        />
      </div>
    );
  }
}
